import { provide, inject, ref, computed, watch } from 'vue';
import { TileModel } from '@/types/B2CcontentServerContract';
import { useCheckout as useB2CCheckout } from '@/b2c/checkout/useCheckout';
import authStore from '@/shared/auth/auth.store';
import { useCheckout as useB2BCheckout } from '@/b2b/checkout/useCheckout';
const isB2B = computed<boolean>(() => authStore.isSignedInAsPartner);
const useCheckout = isB2B.value ? useB2BCheckout : useB2CCheckout;

const providePaymentOptionsScope: string = 'ProvidePaymentOptionsScope';
export enum PaymentProvider {
    AltaPay = 0,
    BankTransfer = 1,
    HasSufficientPayments = 2,
}

export function providePaymentOptions(options: TileModel[]): void {
    provide(providePaymentOptionsScope, options);
}

const selectedProvider = ref<PaymentProvider | undefined>();
const providerError = ref<boolean>(false);

export function usePaymentOptions() {
    const options = inject<TileModel[]>(providePaymentOptionsScope);
    if (!options) {
        throw new Error(`No ${providePaymentOptionsScope} provided`);
    }

    const { hasSufficientPayments } = useCheckout();

    const isAltaPayProviderSelected = computed<boolean>(() => selectedProvider.value === PaymentProvider.AltaPay);
    const isBankTransferProviderSelected = computed<boolean>(() => selectedProvider.value === PaymentProvider.BankTransfer);
    const isHasSufficientPaymentsProviderSelected = computed<boolean>(() => selectedProvider.value === PaymentProvider.HasSufficientPayments);

    const setSelectedProvider = (provider: PaymentProvider) => {
        selectedProvider.value = provider;
    };

    const clearSelectedProvider = () => {
        selectedProvider.value = undefined;
    };

    const setProviderError = () => {
        providerError.value = true;
    };

    const clearProviderError = () => {
        providerError.value = false;
    };

    watch(hasSufficientPayments, (hasSufficient) => {
        if (hasSufficient) {
            setSelectedProvider(PaymentProvider.HasSufficientPayments);
        } else if (isHasSufficientPaymentsProviderSelected.value) {
            clearSelectedProvider();
        }
    }, { immediate: true });

    return {
        options,
        selectedProvider,
        isAltaPayProviderSelected,
        isBankTransferProviderSelected,
        isHasSufficientPaymentsProviderSelected,
        providerError,
        setSelectedProvider,
        clearSelectedProvider,
        setProviderError,
        clearProviderError,
    };
}
